import { DialogContent } from '@mui/material';
import { Stack } from '@mui/system';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { forwardRef, PropsWithChildren } from 'react';

import { LegacyResponseApplicationError } from '@verantos-dev/types-common';

import { ApiWorkflowDialogSteps } from './enums';
import type { ApiWorkflowDialogMessages } from './types';
import { useApiWorkflowDialogContent } from './useApiWorkflowDialogContent';
import { LoadingSpinner } from '../../../LoadingSpinner/LoadingSpinner';
import { DialogMessageBox } from '../../components/DialogMessageBox';
import { SuccessCountdownMessage } from '../../components/SuccessCountdownMessage';
import { ApiWorkflowDialogProps } from '../../types';

export interface ApiCreateWorkflowDialogContentProps
  extends Pick<
    ApiWorkflowDialogProps,
    'onClose' | 'successCountdownTime' | 'hideDisplayButtonsOnSuccess'
  > {
  /** id of the dialog */
  id: string;

  /**
   * @prop {style} style - The style props to apply to override the default styles
   */
  style?: React.CSSProperties;

  /** The current step of the dialog */
  currentStep: ApiWorkflowDialogSteps;

  /** The message overrides to display in the dialog */
  dialogMessages?: ApiWorkflowDialogMessages;

  /** The error that occurred during submission */
  submittingError?:
    | LegacyResponseApplicationError[]
    | FetchBaseQueryError
    | SerializedError
    | undefined;

  /** Whether the dialog is currently submitting */
  isSubmitting?: boolean;

  /** The error that occurred during initial loading */
  initialLoadingError?:
    | LegacyResponseApplicationError[]
    | FetchBaseQueryError
    | SerializedError
    | undefined;

  /** Whether the dialog is currently loading */
  isInitialLoading?: boolean;

  /** The function to override the render the submitting state */
  renderSubmitting?: React.ReactNode;

  /** To add extra information to the dialog after the data is submitted successfully along with success message */
  renderSubmitted?: React.ReactNode;

  /** The function to call to handle redirect after submission */
  onRedirectAfterSubmit?: () => void;

  /** The function to retry loading the initial data, if it fails */
  renderConfirmationStep?: React.ReactNode;
}

export const ApiWorkflowDialogContent = forwardRef<
  HTMLElement,
  PropsWithChildren<ApiCreateWorkflowDialogContentProps>
>(
  (
    {
      id,
      style,
      currentStep,
      dialogMessages,
      initialLoadingError,
      submittingError,
      isInitialLoading,
      isSubmitting,
      children,
      renderSubmitting,
      renderSubmitted,
      renderConfirmationStep,
      successCountdownTime,
      hideDisplayButtonsOnSuccess,
      onClose,
      onRedirectAfterSubmit,
    },
    ref
  ) => {
    const {
      initialLoadingTitle,
      submittedTitle,
      submittedText,
      submittingErrorText,
      submittingErrorTitle,
      initialLoadingErrorText,
      initialLoadingErrorTitle,
    } = useApiWorkflowDialogContent({
      id,
      currentStep,
      dialogMessages,
      initialLoadingError,
      submittingError,
      isInitialLoading,
      isSubmitting,
      renderSubmitting,
      renderConfirmationStep,
    });

    if (initialLoadingError) {
      return (
        <DialogContent style={style} data-testid={`dialog-${id}-content`}>
          <DialogMessageBox
            id="fetch-error"
            title={initialLoadingErrorTitle}
            type="error"
            text={initialLoadingErrorText}
          />
        </DialogContent>
      );
    }

    if (submittingError) {
      return (
        <DialogContent style={style} data-testid={`dialog-${id}-content`}>
          <DialogMessageBox
            id="submit-error"
            title={submittingErrorTitle}
            type="error"
            text={submittingErrorText}
          />
        </DialogContent>
      );
    }

    if (isInitialLoading) {
      return (
        <DialogContent style={style} data-testid={`dialog-${id}-content`}>
          <LoadingSpinner id="datasetLoading" title={initialLoadingTitle} />
        </DialogContent>
      );
    }

    if (currentStep === ApiWorkflowDialogSteps.UserConfirmation && renderConfirmationStep) {
      return (
        <DialogContent style={style} data-testid={`dialog-${id}-content`}>
          {renderConfirmationStep}
        </DialogContent>
      );
    }

    if (currentStep === ApiWorkflowDialogSteps.UserSubmitted && isSubmitting && renderSubmitting) {
      return (
        <DialogContent style={style} data-testid={`dialog-${id}-content`}>
          {renderSubmitting}
        </DialogContent>
      );
    }

    if (currentStep === ApiWorkflowDialogSteps.UserSubmitted && !isSubmitting) {
      return (
        <DialogContent style={style} data-testid={`dialog-${id}-content`}>
          <Stack gap={4}>
            <DialogMessageBox
              id="submitted"
              title={submittedTitle}
              type="success"
              text={submittedText}
              renderSubmitted={renderSubmitted}
            />
            <SuccessCountdownMessage
              successCountdownTime={successCountdownTime}
              hideDisplayButtonsOnSuccess={hideDisplayButtonsOnSuccess}
              onClose={onClose}
              onRedirectAfterSubmit={onRedirectAfterSubmit}
            />
          </Stack>
        </DialogContent>
      );
    }

    return (
      <DialogContent style={style} ref={ref} data-testid={`dialog-${id}-content`}>
        {currentStep === ApiWorkflowDialogSteps.UserEntry && children}
        {currentStep === ApiWorkflowDialogSteps.UserSubmitted && isSubmitting && children}
      </DialogContent>
    );
  }
);
